<template>
  <div class="mb-14">
    <!--  BERUF  -->
    <section class="w-full flex justify-center mb-14">
      <div class="flex flex-col gap-5 w-full sm:w-1/2 md:w-1/3">
        <ElFormItem
          id="formError.beruf"
          label="Beruf"
          for="Beruf"
          :error="formErrors.beruf?.[0]"
          label-position="top"
        >
          <ElInput
            id="Beruf"
            v-model="form.beruf"
          />
        </ElFormItem>

        <ElFormItem
          v-for="index in berufStatusCount"
          id="formError.taetigkeitsstatus"
          :key="index"
          :label="`${index === 1 ? '': `${index}. `}Berufsstatus`"
          :error="formErrors.taetigkeitsstatus?.[index - 1]"
          label-position="top"
        >
          <div class="flex flex-row justify-between items-center gap-2 w-full">
            <ElSelect
              :model-value="form.taetigkeitsstatus[index - 1]"
              placeholder=""
              required
              class="w-full"
              @update:model-value="(value) => setTaetigkeit(index - 1, value)"
            >
              <ElOption
                v-for="option in taetigkeitsstatus"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </ElSelect>
            <AppButton
              v-if="index > 1 && index >= form.taetigkeitsstatus.length"
              title="Berufstatus entfernen"
              type="primary"
              text
              bg
              circle
              class="shrink-0"
              @click="removeBerufStatusField(index)"
            >
              <FaIcon name="minus" />
            </AppButton>
            <AppButton
              v-if="index === berufStatusCount && berufStatusCount < 3 && index === form.taetigkeitsstatus.length"
              title="Weiteren Berufstatus hinzüfugen"
              type="primary"
              text
              bg
              circle
              class="shrink-0"
              @click="addNewBerufStatusField"
            >
              <FaIcon name="plus" />
            </AppButton>
          </div>
        </ElFormItem>
      </div>
    </section>

    <!-- EINKOMMENSDATEN -->
    <section class="flex flex-col items-center mb-14">
      <h2 class="checkup-h2">
        Mein Einkommen beträgt
      </h2>
      <ElAlert
        type="info"
        class="text-left max-w-lg mt-2 mb-6 el-alert--primary"
        title="Hinweis"
        show-icon
      >
        Falls das Brutto- oder Nettoeinkommen nicht bekannt ist, {{ duzen('kannst du', 'können Sie') }} auch nur
        einen der beiden Werte eintragen.
      </ElAlert>

      <div class="w-full sm:w-1/2 md:w-1/3 mb-5">
        <ElFormItem
          label="Bruttoeinkommen"
          for="Bruttoeinkommen"
          :error="formErrors.brutto?.[0]"
          label-position="top"
        >
          <MoneyInput
            id="Bruttoeinkommen"
            :model-value="form.brutto"
            as-string
            @update:model-value="set('brutto', $event)"
          />
        </ElFormItem>
      </div>
      <div class="w-full sm:w-1/2 md:w-1/3 mb-7">
        <ElRadioGroup
          :model-value="form.isBruttoJahresGehalt ?? undefined"
          class="w-full bg-white"
          :class="isMobile ? 'el-radio-group--vertical' : 'el-radio-group--fill'"
          @update:model-value="form.isBruttoJahresGehalt = $event === true"
        >
          <ElRadioButton :value="true">
            im Jahr
          </ElRadioButton>

          <ElRadioButton :value="false">
            im Monat
          </ElRadioButton>
        </ElRadioGroup>
      </div>

      <div class="w-full sm:w-1/2 md:w-1/3 mb-5">
        <ElFormItem
          label="Nettoeinkommen"
          :error="formErrors.netto?.[0]"
          label-position="top"
        >
          <MoneyInput
            :model-value="form.netto"
            as-string
            @update:model-value="set('netto', $event)"
          />
        </ElFormItem>
      </div>

      <div class="w-full sm:w-1/2 md:w-1/3">
        <ElRadioGroup
          :model-value="form.isNettoJahresGehalt ?? undefined"
          class="w-full bg-white"
          :class="isMobile ? 'el-radio-group--vertical' : 'el-radio-group--fill'"
          @update:model-value="set('isNettoJahresGehalt', $event === true)"
        >
          <ElRadioButton :value="true">
            im Jahr
          </ElRadioButton>

          <ElRadioButton :value="false">
            im Monat
          </ElRadioButton>
        </ElRadioGroup>
      </div>
    </section>

    <!-- KRANKENVERSICHERUNGSSTATUS -->
    <section class="flex flex-col items-center">
      <div class="flex flex-col items-center justify-center">
        <h2 class="mb-3 md:mb-5 checkup-h2">
          Mein Krankenversicherungsstatus:
        </h2>
        <FieldError
          v-for="error in formErrors.krankenversicherungsstatus"
          :key="error"
        >
          {{ error }}
        </FieldError>
      </div>

      <div class="w-full flex justify-center">
        <ElRadioGroup
          :model-value="form.krankenversicherungsstatus ?? undefined"
          class="w-full lg:w-2/3 bg-white"
          :class="isMobile ? 'el-radio-group--vertical' : 'el-radio-group--fill'"
          @update:model-value="form.krankenversicherungsstatus = Number($event)"
        >
          <ElRadioButton
            v-for="{ label, value } in krankenversicherungsstatus"
            :key="value"
            :value="value"
          >
            {{ label }}
          </ElRadioButton>
        </ElRadioGroup>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import FieldError from '@/application/components/ui/FieldError.vue';
import AppButton from '@/application/components/ui/AppButton.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ElAlert } from 'element-plus';
import { ElFormItem, ElInput, ElOption, ElRadioButton, ElRadioGroup, ElSelect } from 'element-plus';
import { sortBy } from 'lodash-es';

import { useDetectMobile } from '@/application/composables/detectMobile';
import { toLabelAndValue } from '@/checkup/utils/demvWerte';
import { demvWerte, useStep } from '@/checkup/state';
import { Step } from '@/checkup/utils/steps';
import MoneyInput from '@/application/components/form-elements/MoneyInput/MoneyInput.vue';
import { onMounted, ref } from 'vue';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

const { isMobile } = useDetectMobile();
const { form, formErrors, set } = useStep(Step.BERUF);
const { duzen } = useBaseDataStore();

const berufStatusCount = ref(1);

const taetigkeitsstatus = sortBy(
  toLabelAndValue(demvWerte.taetigkeitstatus),
  'label',
);
const krankenversicherungsstatus = toLabelAndValue(
  demvWerte.krankenversicherungsstatus,
);

onMounted(() => {
  let length = form.value.taetigkeitsstatus.length
  // when empty, we have to set initial value
  if (length === 0) {
    length++;
  }
  berufStatusCount.value = length
})
const addNewBerufStatusField = () => {
  berufStatusCount.value++;
};

const removeBerufStatusField = (index: number) => {
  form.value.taetigkeitsstatus.splice(index - 1, 1)
  berufStatusCount.value--;
};

const setTaetigkeit = (index, value) => {
  (form.value.taetigkeitsstatus ?? []).splice(index, 1, value);
};
</script>
